import React, { useEffect, useState } from 'react';
import { getRequest, postRequest, showMessage } from '../../config';
import './autoFill.css';

/**
 * ReviewQueue - Shows low-confidence auto-filled answers for review
 *
 * Props:
 * - companyId: Company ID
 * - moduleType: Optional module type filter
 * - onReviewComplete: Callback when review actions are taken
 * - isOpen: Whether the modal/panel is open
 * - onClose: Callback to close
 */
const ReviewQueue = ({ companyId, moduleType, onReviewComplete, isOpen, onClose }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(null);

    const fetchReviewQueue = async () => {
        if (!companyId) return;

        setLoading(true);
        try {
            let url = `/clarence/auto-fill/review-queue/${companyId}`;
            if (moduleType) url += `?moduleType=${moduleType}`;

            const response = await getRequest(url);
            if (response?.code === 200) {
                setItems(response?.body?.items || []);
            }
        } catch (error) {
            console.error('Failed to fetch review queue:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchReviewQueue();
        }
    }, [isOpen, companyId, moduleType]);

    const handleReview = async (answerId, action, modifiedAnswer = null) => {
        setActionLoading(answerId);
        try {
            const payload = { answerId, action };
            if (modifiedAnswer) payload.modifiedAnswer = modifiedAnswer;

            const response = await postRequest('/clarence/auto-fill/review', payload);

            if (response?.code === 200) {
                showMessage(`Answer ${action}d successfully`);
                // Remove item from list
                setItems(prev => prev.filter(item => item._id !== answerId));
                onReviewComplete?.(response?.body);
            } else {
                showMessage(response?.message || 'Review action failed');
            }
        } catch (error) {
            console.error('Review action failed:', error);
            showMessage('Review action failed');
        } finally {
            setActionLoading(null);
        }
    };

    const getConfidenceClass = (confidence) => {
        if (confidence < 0.4) return 'low';
        return 'medium';
    };

    if (!isOpen) return null;

    return (
        <div className="auto-fill-progress-overlay" onClick={onClose}>
            <div
                className="auto-fill-progress-modal"
                style={{ maxWidth: '600px', maxHeight: '80vh', overflow: 'auto' }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="review-queue-header">
                    <h2>Review Queue</h2>
                    {items.length > 0 && (
                        <span className="review-queue-count">{items.length} pending</span>
                    )}
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '1.5rem',
                            color: '#6B7280',
                            marginLeft: 'auto'
                        }}
                    >
                        &times;
                    </button>
                </div>

                {loading ? (
                    <div className="review-queue-empty">Loading...</div>
                ) : items.length === 0 ? (
                    <div className="review-queue-empty">
                        <p>No items pending review</p>
                        <p style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>
                            All auto-filled answers have been reviewed or had high confidence.
                        </p>
                    </div>
                ) : (
                    items.map((item) => (
                        <div key={item._id} className="review-queue-item">
                            <div className="review-queue-item-header">
                                <span className="review-queue-item-qno">{item.qNo}</span>
                                <div className="review-queue-item-confidence">
                                    <span className={`confidence-badge ${getConfidenceClass(item.confidence)}`}>
                                        {Math.round(item.confidence * 100)}% confidence
                                    </span>
                                </div>
                            </div>

                            <div className="review-queue-item-question">{item.question}</div>

                            <div className="review-queue-item-answer">
                                <span className="review-queue-item-answer-label">Suggested Answer:</span>
                                <span className="review-queue-item-answer-value">{item.suggestedAnswer}</span>
                            </div>

                            {item.comment && (
                                <div className="review-queue-item-explanation">
                                    {item.comment.replace('[Auto-filled by Clarence] ', '')}
                                </div>
                            )}

                            {item.sources && item.sources.length > 0 && (
                                <div className="review-queue-item-sources">
                                    {item.sources.map((source, idx) => (
                                        <span key={idx} className="review-queue-item-source">{source}</span>
                                    ))}
                                </div>
                            )}

                            <div className="review-queue-item-actions">
                                <button
                                    className="review-queue-btn review-queue-btn-reject"
                                    onClick={() => handleReview(item._id, 'reject')}
                                    disabled={actionLoading === item._id}
                                >
                                    Reject
                                </button>
                                <button
                                    className="review-queue-btn review-queue-btn-approve"
                                    onClick={() => handleReview(item._id, 'approve')}
                                    disabled={actionLoading === item._id}
                                >
                                    {actionLoading === item._id ? 'Processing...' : 'Approve'}
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default ReviewQueue;
